import { lazy, Suspense, useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";

import RouteFallback from "./components/RouteFallback";
import ScrollToTop from "./components/ScrollToTop";
import MetaPixel from "./components/MetaPixel";
import CookieBanner from "./components/CookieBanner/CookieBanner";
import LandingPage from "./pages/LandingPage/LandingPage";
import NotFoundPage from "./pages/NotFoundPage/NotFoundPage";
import PoliticaConfidentialitatePage from "./pages/Legal/PoliticaConfidentialitatePage";
import PoliticaGdprPage from "./pages/Legal/PoliticaGdprPage";
import PoliticaLivrarePage from "./pages/Legal/PoliticaLivrarePage";
import PoliticaRambursarePage from "./pages/Legal/PoliticaRambursarePage";
import TermeniSiConditiiPage from "./pages/Legal/TermeniSiConditiiPage";
import GhidAnafPage from "./pages/Articles/GhidAnafPage";
import GhiduriPage from "./pages/Articles/GhiduriPage";
import MasinaPersonalaSrlPage from "./pages/Articles/MasinaPersonalaSrlPage";
import RegulamentInternAutoturismPage from "./pages/Articles/RegulamentInternAutoturismPage";
import BonFiscalVsFacturaPage from "./pages/Articles/BonFiscalVsFacturaPage";
import Deducere100Page from "./pages/Articles/Deducere100Page";
import FoiParcursPfaPage from "./pages/Articles/FoiParcursPfaPage";
import ModelFoaieParcursPage from "./pages/Articles/ModelFoaieParcursPage";
import ControlAnafAutoturismPage from "./pages/Articles/ControlAnafAutoturismPage";
import AutomatizareFoiParcursPage from "./pages/Articles/AutomatizareFoiParcursPage";
import FoiParcursComodatPage from "./pages/Articles/FoiParcursComodatPage";
import FoaieParcursCasaServiciuPage from "./pages/Articles/FoaieParcursCasaServiciuPage";
import NormaDeConsumPage from "./pages/Articles/NormaDeConsumPage";
import AutoutilitaraVsAutoturismPage from "./pages/Articles/AutoutilitaraVsAutoturismPage";
import DecontKilometriPage from "./pages/Articles/DecontKilometriPage";
import TrendDeductibilitate100Page from "./pages/Articles/TrendDeductibilitate100Page";
import MasinaFirmaAngajatiPage from "./pages/Articles/MasinaFirmaAngajatiPage";
import VanzareMasinaFoaieParcursPage from "./pages/Articles/VanzareMasinaFoaieParcursPage";
import LeasingOperationalFoaieParcursPage from "./pages/Articles/LeasingOperationalFoaieParcursPage";
import { authAPI } from "./api/auth";
import { redirectToAuthenticatedAppHome } from "./utils/authRedirect";

/**
 * App pages are lazy so the public site (landing / ghiduri / legal) does not
 * download dashboard, Excel, maps or admin. Public pages stay eager so
 * prerendered HTML hydrates without a Suspense flash.
 */
const LoginPage = lazy(() => import("./pages/LoginPage/LoginPage"));
const VerifyEmailPage = lazy(() => import("./pages/LoginPage/VerifyEmailPage"));
const ForgotPasswordPage = lazy(() => import("./pages/LoginPage/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/LoginPage/ResetPasswordPage"));
const MainLayout = lazy(() => import("./components/layout/MainLayout"));
const DashboardPage = lazy(() => import("./pages/DashboardPage/DashboardPage"));
const GenerarePageV2 = lazy(() => import("./pages/GenerarePageV2/GenerarePageV2"));
const AlimentariPage = lazy(() => import("./pages/AlimentariPage/AlimentariPage"));
const PunctePage = lazy(() => import("./pages/PunctePage/PunctePage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage/ProfilePage"));
const AsistentaPage = lazy(() => import("./pages/AsistentaPage/AsistentaPage"));
const PlataSuccesPage = lazy(() => import("./pages/PlataPage/PlataSuccesPage"));
const PlataEsecPage = lazy(() => import("./pages/PlataPage/PlataEsecPage"));
const AdminLayout = lazy(() => import("./pages/AdminPage/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/AdminPage/AdminDashboard"));
const AdminUsers = lazy(() => import("./pages/AdminPage/AdminUsers"));
const AdminUserDetail = lazy(() => import("./pages/AdminPage/AdminUserDetail"));
const AdminFirmeSearch = lazy(() => import("./pages/AdminPage/AdminFirmeSearch"));
const AdminFirmeResult = lazy(() => import("./pages/AdminPage/AdminFirmeResult"));
const AdminPlati = lazy(() => import("./pages/AdminPage/AdminPlati"));
const AdminFacturare = lazy(() => import("./pages/AdminPage/AdminFacturare"));
const AdminPreturiCombustibil = lazy(
  () => import("./pages/AdminPage/AdminPreturiCombustibil"),
);
const AuthedAppShell = lazy(() => import("./app/AuthedAppShell"));
const FoiRoot = lazy(() =>
  import("./app/AuthedAppShell").then((module) => ({ default: module.FoiRoot })),
);
const PaymentRoot = lazy(() =>
  import("./app/AuthedAppShell").then((module) => ({ default: module.PaymentRoot })),
);

function NotFoundRoute() {
  const location = useLocation();
  const normalizedPath = location.pathname.replace(/\/+$/, "") || "/";

  if (normalizedPath !== location.pathname) {
    return <Navigate to={`${normalizedPath}${location.search}${location.hash}`} replace />;
  }

  return <NotFoundPage />;
}

function App() {
  /** Align with Vite `base` so deep links and client routes work when the app is not at domain root. */
  const normalizedBase = (import.meta.env.BASE_URL.replace(/\/$/, "") || "/");
  const routerBasename = normalizedBase === "/" ? undefined : normalizedBase;

  return (
    <BrowserRouter basename={routerBasename}>
      <ScrollToTop />
      <MetaPixel />
      <CookieBanner />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Auth routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth/verifica-email" element={<VerifyEmailPage />} />
          <Route path="/auth/am-uitat-parola" element={<ForgotPasswordPage />} />
          <Route path="/auth/reseteaza-parola" element={<ResetPasswordPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />

          {/* Payment return pages */}
          <Route
            path="/plata/succes"
            element={
              <PaymentRoot>
                <PlataSuccesPage />
              </PaymentRoot>
            }
          />
          <Route
            path="/plata/esec"
            element={
              <PaymentRoot>
                <PlataEsecPage />
              </PaymentRoot>
            }
          />

          {/* Aplicație autentificată: același ProtectedRoute + HeaderUserProvider pentru /foi și /admin */}
          <Route element={<AuthedAppShell />}>
            <Route path="/foi" element={<FoiRoot />}>
              <Route index element={<Navigate to="gestiune" replace />} />

              <Route element={<MainLayout />}>
                <Route path="gestiune" element={<DashboardPage />} />
                <Route path="puncte" element={<PunctePage />} />
                <Route path="alimentari" element={<AlimentariPage />} />
                <Route path="profil" element={<ProfilePage />} />
                <Route path="asistenta" element={<AsistentaPage />} />
              </Route>

              <Route path="generare-v2" element={<GenerarePageV2 />} />
            </Route>

            {/* Fără verificare client isAdmin: redirecționa la /foi/gestiune și părea „refresh”. API /admin/* rămâne protejat pe server. */}
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="users/:userId" element={<AdminUserDetail />} />
              <Route path="firme" element={<AdminFirmeSearch />} />
              <Route path="firme/rezultat/:userId" element={<AdminFirmeResult />} />
              <Route path="plati" element={<AdminPlati />} />
              <Route path="facturare" element={<AdminFacturare />} />
              <Route
                path="preturi-combustibil"
                element={<AdminPreturiCombustibil />}
              />
            </Route>
          </Route>

          {/* Legal / marketing documents (same SPA as landing) */}
          <Route path="/despre" element={<Navigate to={{ pathname: "/", hash: "#visajtech" }} replace />} />
          <Route path="/termeni-si-conditii" element={<TermeniSiConditiiPage />} />
          <Route
            path="/politica-de-confidentialitate"
            element={<PoliticaConfidentialitatePage />}
          />
          <Route path="/politica-gdpr" element={<PoliticaGdprPage />} />
          <Route path="/politica-de-livrare" element={<PoliticaLivrarePage />} />
          <Route path="/politica-de-rambursare" element={<PoliticaRambursarePage />} />

          {/* SEO content / articles */}
          <Route path="/ghiduri" element={<GhiduriPage />} />
          <Route path="/ghid-foaie-parcurs-anaf" element={<GhidAnafPage />} />
          <Route path="/deducere-100-combustibil" element={<Deducere100Page />} />
          <Route path="/foi-parcurs-pfa" element={<FoiParcursPfaPage />} />
          <Route path="/model-foaie-parcurs" element={<ModelFoaieParcursPage />} />
          <Route
            path="/control-anaf-cheltuieli-autoturism"
            element={<ControlAnafAutoturismPage />}
          />
          <Route
            path="/automatizare-foi-de-parcurs"
            element={<AutomatizareFoiParcursPage />}
          />
          <Route path="/foi-parcurs-comodat" element={<FoiParcursComodatPage />} />
          <Route
            path="/foaie-parcurs-casa-serviciu"
            element={<FoaieParcursCasaServiciuPage />}
          />
          <Route
            path="/norma-de-consum-foaie-parcurs"
            element={<NormaDeConsumPage />}
          />
          <Route
            path="/autoutilitara-vs-autoturism-deductibilitate"
            element={<AutoutilitaraVsAutoturismPage />}
          />
          <Route
            path="/masina-personala-srl-comodat"
            element={<MasinaPersonalaSrlPage />}
          />
          <Route
            path="/regulament-intern-autoturism"
            element={<RegulamentInternAutoturismPage />}
          />
          <Route
            path="/bon-fiscal-vs-factura-combustibil"
            element={<BonFiscalVsFacturaPage />}
          />
          <Route path="/decont-kilometri" element={<DecontKilometriPage />} />
          <Route
            path="/trend-deductibilitate-100-procente"
            element={<TrendDeductibilitate100Page />}
          />
          <Route
            path="/masina-firma-angajati"
            element={<MasinaFirmaAngajatiPage />}
          />
          <Route
            path="/vanzare-masina-foaie-parcurs"
            element={<VanzareMasinaFoaieParcursPage />}
          />
          <Route
            path="/leasing-operational-foaie-parcurs"
            element={<LeasingOperationalFoaieParcursPage />}
          />

          {/* Public root page */}
          <Route path="/" element={<LandingPage />} />
          <Route path="*" element={<NotFoundRoute />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

/**
 * Handle Google OAuth callback.
 * The server sets an httpOnly cookie during the redirect,
 * so we just need to navigate to the app — no token in URL.
 */
function AuthCallback() {
  const navigate = useNavigate();

  useEffect(() => {
    authAPI
      .getMe()
      .then(() => {
        redirectToAuthenticatedAppHome();
      })
      .catch(() => {
        navigate("/login", { replace: true });
      });
  }, [navigate]);

  return <div>Autentificare în curs...</div>;
}

export default App;
